// Generated by GitHub Copilot
// US1794886: Brand API client for fetching brand data
import configuration from "./index";
import { authFetch } from "../utils/authFetch";

/**
 * Get CSRF token from meta tag or cookie
 * @returns {string|null} CSRF token or null if not found
 */
function getCSRFToken() {
  // Try meta tag first (Rails/Django style)
  const metaToken = document.querySelector('meta[name="csrf-token"]')?.content;
  if (metaToken) return metaToken;

  // Fallback to cookie (if backend sets one)
  const cookieMatch = document.cookie.match(/CSRF-TOKEN=([^;]+)/);
  return cookieMatch ? cookieMatch[1] : null;
}

/**
 * Centralized error handler for Brand API calls
 * - Sanitizes error logging (detailed errors only in development)
 * - Provides generic user-facing messages
 * @param {Response} response - Fetch response object
 * @param {string} operation - Operation being performed (e.g., "create", "update")
 * @throws {Error} User-friendly error message
 */
async function handleBrandAPIError(response, operation) {
  if (!response.ok) {
    const errorText = await response.text();

    // Only log detailed errors in development mode
    if (process.env.NODE_ENV === 'development') {
      console.error(`Brand API error (${operation}):`, errorText);
    }

    // Generic user-facing error (never expose backend details)
    throw new Error(`Failed to ${operation} brand. Please try again.`);
  }
}

export const BrandsAPI = {
  /**
   * Fetches all brands from the API
   * @returns {Promise<{brands: Array<Brand>, count: number}>} Brand list response containing array of brands and total count
   * @throws {Error} If the API request fails or returns a non-ok response
   */
  get: async function () {
    const brandsAPI = configuration.apiBase + `/brands`;
    // Generated by GitHub Copilot - Use authFetch to handle 401 errors
    const response = await authFetch(brandsAPI, {
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });
    await handleBrandAPIError(response, 'fetch');
    return await response.json();
  },
  /**
   * Creates a new brand
   * @param {Object} brandData - Brand data to create
   * @param {string} brandData.name - Brand name (required, max 50 chars)
   * @param {Array} [brandData.workflows] - PagerDuty workflow associations
   * @returns {Promise<Brand>} Created brand object with id and all properties
   * @throws {Error} If validation fails or API request fails
   */
  // US1873540: Brand Creation
  create: async function (brandData) {
    const brandsAPI = configuration.apiBase + `/brands`;
    const csrfToken = getCSRFToken();

    const response = await authFetch(brandsAPI, {
      method: "POST",
      body: JSON.stringify(brandData),
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
        ...(csrfToken && { 'X-CSRF-Token': csrfToken })
      }
    });
    await handleBrandAPIError(response, 'create');
    return await response.json();
  },
  /**
   * Updates an existing brand
   * @param {string} brandId - Unique identifier of the brand to update (required, URL-encoded)
   * @param {Object} brandData - Brand data to update
   * @param {string} [brandData.name] - Brand name (optional, max 50 chars)
   * @param {Array} [brandData.workflows] - PagerDuty workflow associations
   * @returns {Promise<Brand>} Updated brand object with all current properties
   * @throws {Error} If brand not found, validation fails, or API request fails
   */
  // US1873540: Brand Update
  update: async function (brandId, brandData) {
    const brandsAPI = configuration.apiBase + `/brands/${encodeURIComponent(brandId)}`;
    const csrfToken = getCSRFToken();

    const response = await authFetch(brandsAPI, {
      method: "PUT",
      body: JSON.stringify(brandData),
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
        ...(csrfToken && { 'X-CSRF-Token': csrfToken })
      }
    });
    await handleBrandAPIError(response, 'update');
    return await response.json();
  },
  /**
   * Deletes a brand by ID
   * @param {string} brandId - Unique identifier of the brand to delete (required, URL-encoded)
   * @returns {Promise<boolean>} Returns true on successful deletion (204 No Content response)
   * @throws {Error} If brand not found, is in use, or API request fails
   */
  // US1873540: Brand Deletion
  delete: async function (brandId) {
    const brandsAPI = configuration.apiBase + `/brands/${encodeURIComponent(brandId)}`;
    const csrfToken = getCSRFToken();

    const response = await authFetch(brandsAPI, {
      method: "DELETE",
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
        ...(csrfToken && { 'X-CSRF-Token': csrfToken })
      }
    });
    // 204 No Content means success
    await handleBrandAPIError(response, 'delete');
    return true;
  },
};
